import { defineComponent as _defineComponent } from 'vue'
import { FieldOptionObj } from 'ah-common-lib/src/form/interfaces';
import { PaginatedQuery, PaginatedResponse, Trade } from 'ah-api-gateways';
import { debounce, DebouncedFunc, uniqBy } from 'lodash';
import { computed, reactive, onBeforeMount } from 'vue';
import { useRequestManager } from 'ah-common-lib/src/requestManager/useRequestManager';
import VueSelect from 'vue-select';
import { usePaymentState } from 'ah-payments';
import { formatDate } from 'ah-common-lib/src/helpers/time';
import { formatCurrencyValue } from 'ah-common-lib/src/helpers/currency';
import ArrowRightIconVue from 'ah-common-lib/src/icons/components/ArrowRightIcon.vue';


export default /*#__PURE__*/_defineComponent({
  __name: 'LinkedToTradeDropdown',
  props: {
    value: { default: null },
    isReadonly: { type: Boolean, default: false }
  },
  emits: ["update:selectedOption"],
  setup(__props: any, { emit }: { emit: ({
  /**
   * - update:validations: emmited when `validations` changes. Can be used with .sync
   */
  (e: 'update:selectedOption', value: any): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { value: Trade | null, isReadonly: boolean };

const paymentState = usePaymentState();





const state = reactive<{
  options: FieldOptionObj[];
  searchTerm: string;
  lastResult: PaginatedResponse<any> | null;
  debouncedSearch: DebouncedFunc<() => void> | null;
  active: boolean;
}>({
  options: [],
  searchTerm: '',
  lastResult: null,
  debouncedSearch: null,
  active: false,
});

onBeforeMount(() => {
  state.debouncedSearch = debounce(() => fetchDataRequest(), 600);
});

const requestManager = useRequestManager({
  exposeToParent: true,
}).manager;

const hasMoreItems = computed(() => {
  if (
    typeof state.lastResult?.pageNumber !== 'number' ||
    typeof state.lastResult?.pageSize !== 'number' ||
    typeof state.lastResult?.total !== 'number'
  ) {
    return false;
  }
  return (state.lastResult.pageNumber + 1) * state.lastResult.pageSize < state.lastResult.total;
});

const loading = computed(() => {
  return requestManager.requestStates['fetchData'] === 'pending';
});

const searchText = computed(() => {
  return `Search for Trades...`;
});

const itemsText = computed(() => {
  if (state.active && !props.value?.referenceNumber) {
    return searchText.value;
  }
  if (props.value?.referenceNumber?.length && props.value?.referenceNumber?.length > 0) {
    return '';
  }
  return 'All';
});

const placeholderClass = computed(() => {
  return ['All', searchText].includes(itemsText.value ?? '') ? '' : 'opacity-full';
});

const options = computed(() => {
  let options = [...state.options];
  if (props.value && props.value?.id && !options.find((option) => option.value === props.value?.id)) {
    options = [props.value, ...options];
  }
  return options;
});

function onTagSearch(search: string) {
  state.searchTerm = search;
  if (state.debouncedSearch) state.debouncedSearch();
}

function onInfiniteScroll(intersection: IntersectionObserverEntry) {
  if (intersection.isIntersecting) {
    const offsetParent = (intersection.target as HTMLElement).offsetParent;
    const scrollTop = offsetParent?.scrollTop;
    if (!requestManager.anyPending) {
      fetchDataRequest(true);
      if (offsetParent && scrollTop) {
        offsetParent.scrollTop = scrollTop;
      }
    }
  }
}

/**
 *
 * Fetch new data and missing data
 *
 */
function fetchDataRequest(infinite = false) {
  const query: PaginatedQuery<any> = {
    pageSize: 20,
    queryBy: 'referenceNumber',
    query: state.searchTerm,
    status: 'OPEN',
    sort: 'settlementDate',
    sortDirection: 'DESC',
    settlementDateFrom: new Date(),
    hedgingProduct: ['FX_SPOT', 'FX_FORWARD', 'FLEXIBLE_FORWARD', 'WINDOW_FORWARD'],
  };

  const infiniteLoad = infinite && typeof state.lastResult?.pageNumber === 'number';

  if (infiniteLoad) {
    query.pageNumber = state.lastResult?.pageNumber! + 1;
  } else {
    query.pageNumber = 0;
    state.lastResult = null;
  }
  let request = paymentState.services.trade.listTrades(query);

  requestManager.currentOrNew('fetchData', request).subscribe((res) => {
    let list: FieldOptionObj<any>[] = infiniteLoad ? [...state.options] : [];
    state.lastResult = res;

    res.list.forEach((r) => {
      list.push({ ...r, label: r.referenceNumber, value: r.id });
    });

    state.options = uniqBy(list, 'value');
  });
}

function setOption(option: any) {
  emit('update:selectedOption', option);
}

return { __sfc: true,paymentState, emit, props, state, requestManager, hasMoreItems, loading, searchText, itemsText, placeholderClass, options, onTagSearch, onInfiniteScroll, fetchDataRequest, setOption, VueSelect, formatDate, formatCurrencyValue, ArrowRightIconVue }
}

})